import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher } from 'react-router';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z } from 'zod';
import { useAtomValue } from 'jotai';
import { Loader2, X } from 'lucide-react';

import { cn } from '@rocco/utils/cn';
import { Form, FormField } from '@rocco/components/form';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@rocco/components/select';
import { useTracker } from '@archipro-website/tracker';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { PopoverAnchor } from '@rocco/components/popover';
import { usePlaceFinder } from '@modules/location/hook/use-place-finder';
import { getClosestBasicPlace } from '@modules/location/util';
import { trackEnquiryFormEngagement } from '~/modules/tracking/util/trackEnquiryFunnelStage';
import { useUserLoggedIn } from '@modules/user';
import { MEMBER_UPDATE_CONTACT_DETAILS_API_ROUTE } from '~/routes/remix-api.member.update-contact-details';
import { useFeature } from '~/modules/root';
import { useGetBuildHubsList } from '@ui-rocco/hooks/use-build-hubs';
import { fileStorage } from '~/utils/file-storage';
import type { PublicUser } from '~/routes/remix-api.user.public-user';
import {
    GATE_FIELD_INPUT_CLASS,
    GateFieldError,
    GateFieldLabel,
    GateInput,
    GateTextarea,
} from '~/modules-rocco/user/component/auth-modal/GateFormControls';
import { GateIdentityFields } from '~/modules-rocco/user/component/auth-modal/GateIdentityFields';
import { GateStickyFooter } from '~/modules-rocco/user/component/auth-modal/GateStickyFooter';
import { PostcodePopover } from '~/modules/enquiry/component/PostcodePopover';
import { FileAttachment } from '~/modules/enquiry/component/FileAttachment';
import { EnquiryProjectDetailsCard } from './EnquiryProjectDetailsCard';
import {
    useEnquiry,
    type EnquiryProfessional,
    type UseEnquiryProps,
} from '~/modules/enquiry/hooks/use-enquiry';
import { useBuildHubFieldSelection } from '~/modules/enquiry/hooks/use-build-hub-field-selection';
import type { Branch } from '~/modules/enquiry/util/extract-branch-from-network.server';

export interface EnquiryGateFormProps {
    className?: string;
    /** The professional to submit an enquiry to. */
    professional: EnquiryProfessional & { CustomEnquiryMessage?: string };
    /** The type of the Library Item (professional, product, project, article). */
    itemType: UseEnquiryProps['itemType'];
    /** The ID of the Library Item (professional, product, project, article). */
    siteTreeID: UseEnquiryProps['siteTreeID'];
    /**
     * Bulk selection, submitted in place of the single id above. The backend
     * splits these by owner so each professional receives only their own items.
     */
    siteTreeIDs?: UseEnquiryProps['siteTreeIDs'];
    /** The source of the enquiry. */
    source: UseEnquiryProps['source'];
    /** The project details if the enquiry is for a project. Used for tracking. */
    relatedItem?: UseEnquiryProps['relatedItem'];
    /** Include a branch dropdown if the professional has branches. Defaults to `false`. */
    includeBranch?: boolean;
    /** Disable the submit button (e.g. in preview). Defaults to `false`. */
    disableSubmit?: boolean;
    /** The name of the item being enquired about (e.g. product name). Used to pre-fill the message. */
    itemTitle?: string;
    /** The CTA label that opened the form. Used to tailor the pre-filled message. */
    ctaLabel?: string;
    /** Called when a guest submit comes back auth-required, with the recognised public user. */
    onAuthRequired: (publicUser: PublicUser) => void;
    /**
     * "Not you? Switch account" — sign this member out and hand the surface to
     * the auth step. Omitted where the host has nowhere to land afterwards, in
     * which case the action is not offered.
     */
    onSwitchAccount?: () => void;
}

const splitName = (name: string | undefined) => {
    const trimmed = (name ?? '').trim();
    if (!trimmed) return { firstName: '', lastName: '' };
    const [firstName, ...rest] = trimmed.split(/\s+/);
    return { firstName: firstName ?? '', lastName: rest.join(' ') };
};

/** The fields that live behind the identity summary when it is collapsed. */
const IDENTITY_FIELD_NAMES = [
    'name',
    'email',
    'phoneNumber',
    'postcode',
] as const;

/**
 * Enquiry form step of the revamped two-pane gate (BC-4833). Reuses the shared
 * enquiry schema, submit/session logic, postcode autocomplete, file
 * attachments and BuildHub selector; only the layout and field styling are new
 * (the small field tokens from the Figma gating frames, shared with the auth
 * modal). Guest submits that hit the auth wall call `onAuthRequired` so the
 * parent modal can host the auth step in place.
 */
export const EnquiryGateForm = ({
    className,
    professional,
    itemType,
    siteTreeID,
    siteTreeIDs,
    source,
    relatedItem,
    includeBranch = false,
    disableSubmit = false,
    itemTitle,
    ctaLabel,
    onAuthRequired,
    onSwitchAccount,
}: EnquiryGateFormProps) => {
    const { user, isLoggedIn } = useUserLoggedIn();
    const { isDesktop } = useAtomValue(uiContextAtomDisplayMode);
    const tracker = useTracker();

    const isBuildHubEnabled = useFeature('buildhub');
    const isBuildHubEnquiryProjectDetailsEnabled = useFeature(
        'buildhub_enquiry_project_details',
    );
    const canIncludeProjectDetails =
        isBuildHubEnabled &&
        isBuildHubEnquiryProjectDetailsEnabled &&
        isLoggedIn;

    const [postcodeSearch, setPostcodeSearch] = useState('');
    const [isPostcodeSearchOpen, setIsPostcodeSearchOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [files, setFiles] = useState<File[]>([]);
    const [name, setName] = useState({ firstName: '', lastName: '' });
    const [identityExpanded, setIdentityExpanded] = useState(false);
    /** The reader has worked the disclosure; from here the state is theirs. */
    const identityTouchedRef = useRef(false);
    const postcodeInputRef = useRef<HTMLInputElement | null>(null);
    /** Focus target for a `name` error — see the submit handler. */
    const firstNameRef = useRef<HTMLInputElement | null>(null);
    const formRef =
        useRef<ReturnType<typeof useForm<z.infer<typeof formSchema>>>>(
            undefined,
        );

    const [buildHubs, isBuildHubsLoading, fetchBuildHubs] =
        useGetBuildHubsList();

    const sessionId = useMemo(
        () => `${professional.ID}-${siteTreeID || ''}`,
        [professional.ID, siteTreeID],
    );

    const hasBranches = includeBranch && !!professional.branchOptions?.length;
    /**
     * Only where the reader has a decision to make. A required select holding
     * a single option is a chore with one legal answer — it still has to reach
     * the submission, so it is chosen for them below rather than dropped.
     */
    const showBranchPicker =
        hasBranches && professional.branchOptions.length > 1;

    const {
        defaultFormValues,
        formSchema,
        state,
        submitEnquiry,
        saveEnquirySession,
        saveSuburbToProfile,
        messagePlaceholder,
    } = useEnquiry({
        professional,
        itemType,
        siteTreeID,
        siteTreeIDs,
        source,
        relatedItem,
        includeBranch,
        isModal: true,
        itemTitle,
        ctaLabel,
        onSuccess: () => {
            formRef.current?.reset();
            setFiles([]);
            fileStorage.clearFiles(sessionId).catch(console.error);
        },
        onAuthRequired,
    });

    /** A submit is on the wire — the fetcher is loading or posting. */
    const isSending = state !== 'idle';

    useEffect(() => {
        if (!canIncludeProjectDetails) return;
        if (!buildHubs && !isBuildHubsLoading) {
            fetchBuildHubs();
        }
    }, [
        buildHubs,
        fetchBuildHubs,
        canIncludeProjectDetails,
        isBuildHubsLoading,
    ]);

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        disabled: state !== 'idle',
        mode: 'onChange',
        defaultValues: defaultFormValues,
    });
    formRef.current = form;

    // Funnel stage: the user started filling the form (once per rendered form).
    // Watch genuine input events ('change'), not formState.isDirty — restores
    // and programmatic writes must not count as engagement. The name inputs
    // write via setValue (watch reports no type), so syncName marks it too.
    const isFormEngagementTracked = useRef(false);
    const markFormEngagement = useCallback(() => {
        if (isFormEngagementTracked.current) return;
        isFormEngagementTracked.current = true;
        trackEnquiryFormEngagement(tracker, {
            professionalName: professional.Title,
            itemID: siteTreeID,
            itemType,
            source,
            professionalID: professional.ID,
        });
    }, [tracker, professional, itemType, siteTreeID, source]);
    useEffect(() => {
        const subscription = form.watch((_values, { type }) => {
            if (type !== 'change') return;
            markFormEngagement();
        });
        return () => subscription.unsubscribe();
    }, [form, markFormEngagement]);

    const selectedBuildHubId = form.watch('buildHubId');
    const [fieldSelection, setFieldSelection] =
        useBuildHubFieldSelection(selectedBuildHubId);

    useEffect(() => {
        if (canIncludeProjectDetails || !selectedBuildHubId) return;
        form.setValue('buildHubId', undefined, { shouldDirty: true });
        saveEnquirySession({ ...form.getValues(), buildHubId: undefined });
    }, [
        canIncludeProjectDetails,
        form,
        saveEnquirySession,
        selectedBuildHubId,
    ]);

    const {
        changeKeyword: findPlace,
        dropdownOptions: placeOptions,
        loading: isPlaceOptionsLoading,
    } = usePlaceFinder({ limit: 10, debounceMs: 250 });

    const autoSelectBranch = useCallback(
        (postcode: (typeof placeOptions)[number]) => {
            if (
                !hasBranches ||
                !professional.branchOptions.length ||
                form.getValues('branchId')
            ) {
                return;
            }
            const branches = professional.branchOptions.map((p) => p.value);
            const closestPlace = getClosestBasicPlace(
                postcode.value,
                branches,
            ) as Branch | undefined;
            const closestBranch = branches.find(
                (branch) => branch.branchId === closestPlace?.branchId,
            );
            if (closestBranch) {
                form.setValue('branchId', closestBranch.branchId.toString());
            }
        },
        [hasBranches, professional, form],
    );

    const selectPostcode = useCallback(
        (place: (typeof placeOptions)[number] | undefined) => {
            if (place) {
                form.setValue('postcode', place, { shouldDirty: true });
                setPostcodeSearch(place.header);
                autoSelectBranch(place);
                saveSuburbToProfile(place);
            } else {
                // @ts-expect-error - force postcode to be null.
                form.setValue('postcode', null, { shouldDirty: true });
                setPostcodeSearch('');
            }
            setIsPostcodeSearchOpen(false);
            saveEnquirySession({ ...form.getValues(), postcode: place });
        },
        [form, autoSelectBranch, saveEnquirySession, saveSuburbToProfile],
    );

    const autoSelectPostcode = useCallback(() => {
        if (
            postcodeSearch &&
            placeOptions.length &&
            !form.getValues('postcode')
        ) {
            selectPostcode(placeOptions[0]);
            form.clearErrors('postcode');
        }
    }, [postcodeSearch, placeOptions, form, selectPostcode]);

    const selectAvailablePostCode = useCallback(() => {
        if (placeOptions.length && !isPlaceOptionsLoading) {
            const selectedPlace = placeOptions.find(
                (place) =>
                    place.value.addressID ===
                    form.getValues('postcode')?.value.addressID,
            );
            selectPostcode(selectedPlace ?? placeOptions[0]);
        }
    }, [placeOptions, isPlaceOptionsLoading, form, selectPostcode]);

    const handleFileChange = useCallback(
        async (newFiles: File[]) => {
            setFiles(newFiles);
            await fileStorage.saveFiles(sessionId, newFiles);
            saveEnquirySession(form.getValues(), newFiles);
        },
        [sessionId, form, saveEnquirySession],
    );

    useEffect(() => {
        setHighlightedIndex(placeOptions.length > 0 ? 0 : -1);
    }, [placeOptions]);

    useEffect(() => {
        if (document.activeElement !== postcodeInputRef.current) {
            autoSelectPostcode();
        }
    }, [autoSelectPostcode]);

    useEffect(() => {
        if (!form.formState.isDirty && !form.formState.isSubmitted) {
            // Load the saved session data / prefilled defaults.
            form.reset(defaultFormValues);
            setName(splitName(defaultFormValues.name));

            if (!postcodeSearch && form.getValues('postcode')) {
                setPostcodeSearch(form.getValues('postcode').header);
            }

            fileStorage.getFiles(sessionId).then((restoredFiles) => {
                if (restoredFiles.length) {
                    setFiles(restoredFiles);
                }
            });
        }
    }, [defaultFormValues, form, postcodeSearch, sessionId]);

    const syncName = useCallback(
        (firstName: string, lastName: string) => {
            // Only ever called from the name inputs' onChange, so this is
            // genuine typing — setValue doesn't surface through form.watch
            // with a 'change' type, mark engagement here.
            markFormEngagement();
            setName({ firstName, lastName });
            form.setValue('name', `${firstName} ${lastName}`.trim(), {
                shouldDirty: true,
                shouldValidate: form.formState.isSubmitted,
            });
        },
        [form, markFormEngagement],
    );

    const watchedEmail = form.watch('email');
    const watchedPhone = form.watch('phoneNumber');
    const watchedPostcode = form.watch('postcode');

    /**
     * The account behind the prefilled fields. Guests get `null` — there is
     * nothing to summarise, so they see the fields themselves.
     */
    const identity = useMemo(() => {
        if (!isLoggedIn) return null;
        // The name inputs are seeded by the re-hydration effect, so on the
        // first paint the local state is still empty. Fall back to the
        // defaults it is about to be seeded from, or the summary shows the
        // reader's email for a frame where their name belongs.
        const seeded = name.firstName || name.lastName;
        const fallback = splitName(defaultFormValues.name);
        return {
            firstName: seeded ? name.firstName : fallback.firstName,
            lastName: seeded ? name.lastName : fallback.lastName,
            email: watchedEmail ?? '',
            phone: watchedPhone || undefined,
            suburb: watchedPostcode?.header || undefined,
            avatarUrl: user.ProfileImageLink || user.ProfileImage || undefined,
        };
    }, [
        isLoggedIn,
        name,
        defaultFormValues,
        watchedEmail,
        watchedPhone,
        watchedPostcode,
        user,
    ]);

    /**
     * A member who cannot send as-is starts on the fields, not on a summary
     * that hides what is missing — phone and suburb are both required to
     * submit and are the two most often absent from a profile.
     *
     * Tracks the defaults rather than latching on the first pass. The saved
     * draft lives in `sessionStorage` and is not read until after the first
     * client render, so a member whose phone and suburb are in the draft but
     * not on their profile reads as incomplete for one pass; latching there
     * would strand them expanded. Following the defaults lets that correct
     * itself when the draft lands.
     *
     * Stops the moment the reader works the disclosure themselves — from then
     * on the state is theirs, and finishing the last missing field must never
     * collapse the card mid-edit.
     */
    useEffect(() => {
        if (!isLoggedIn || identityTouchedRef.current) return;
        const filled = (value: unknown) =>
            typeof value === 'string' ? !!value.trim() : !!value;
        setIdentityExpanded(
            !filled(defaultFormValues.name) ||
                !filled(defaultFormValues.email) ||
                !filled(defaultFormValues.phoneNumber) ||
                !filled(defaultFormValues.postcode),
        );
    }, [isLoggedIn, defaultFormValues]);

    /**
     * The form's contact details against what the account holds. Comparing to
     * the profile rather than to a snapshot taken when the panel opened means
     * a reader who edits a field and puts it back is not offered a save that
     * writes nothing, and a panel that opened itself does not claim changes
     * nobody made.
     */
    const identitySnapshot = useMemo(
        () => ({
            FirstName: name.firstName.trim(),
            Surname: name.lastName.trim(),
            Phone: (watchedPhone ?? '').trim(),
            Suburb: watchedPostcode ? JSON.stringify(watchedPostcode) : '',
        }),
        [name, watchedPhone, watchedPostcode],
    );
    const identityOnAccount = useMemo(() => {
        const onAccount = splitName(defaultFormValues.name);
        return {
            FirstName: onAccount.firstName.trim(),
            Surname: onAccount.lastName.trim(),
            Phone: (defaultFormValues.phoneNumber ?? '').trim(),
            Suburb: defaultFormValues.postcode
                ? JSON.stringify(defaultFormValues.postcode)
                : '',
        };
    }, [defaultFormValues]);
    const identityChanges = Object.fromEntries(
        Object.entries(identitySnapshot).filter(
            ([field, value]) =>
                // Never an empty one. The write is a partial update, so a
                // blank field posted as `''` is stored as `''` — clearing the
                // phone here to retype it and closing the panel mid-way would
                // wipe the number off the account. Emptying a detail is not
                // something this form can express: all four are required to
                // send, so an empty value is only ever a half-finished edit.
                value !== '' &&
                identityOnAccount[field as keyof typeof identityOnAccount] !==
                    value,
        ),
    );
    const identityDirty = Object.keys(identityChanges).length > 0;

    const contactFetcher = useFetcher();
    const handleIdentityExpandedChange = useCallback(
        (next: boolean) => {
            identityTouchedRef.current = true;
            setIdentityExpanded(next);
            if (next) return;
            // Closing is optimistic: the summary shows the new values straight
            // away and the write goes out behind it. Nothing downstream waits
            // on the profile — the enquiry carries its own copy of these
            // fields — so a write that fails costs the reader nothing here
            // beyond having to correct the same detail again next time.
            if (Object.keys(identityChanges).length) {
                contactFetcher.submit(identityChanges, {
                    method: 'POST',
                    action: MEMBER_UPDATE_CONTACT_DETAILS_API_ROUTE,
                });
            }
        },
        [contactFetcher, identityChanges],
    );

    const errors = form.formState.errors;
    const emailDisabled = isLoggedIn && !!user.Email;
    const emailReg = form.register('email');
    const phoneReg = form.register('phoneNumber');
    const messageReg = form.register('message');

    const inputClass = cn(GATE_FIELD_INPUT_CLASS, 'w-full');

    return (
        <div
            className={cn(
                // `flex-1` so a short form still fills the pane and the CTA
                // can sit against the bottom edge rather than floating under
                // the last field. A signed-in reader's form is short: five
                // fields collapse to one row.
                'mx-auto flex w-full max-w-93.75 flex-1 flex-col',
                className,
            )}
        >
            {/* FormProvider context for Rocco form primitives rendered inside
                (BuildHubSelector's FormItem/FormLabel read it via useFormField). */}
            <Form {...form}>
                <form
                    suppressHydrationWarning
                    id="enquiry-gate-form"
                    onSubmit={form.handleSubmit(
                        (data) => {
                            if (state !== 'idle') return;
                            submitEnquiry(data, files, fieldSelection);
                        },
                        (submitErrors) => {
                            // An invalid contact field is unreachable behind a
                            // collapsed summary. Open it and put the cursor in
                            // the offending field: the reader pressed Continue
                            // from the pinned footer, so a panel that expands
                            // above the fold without focus moving reads as the
                            // button having done nothing.
                            const invalidIdentityField =
                                IDENTITY_FIELD_NAMES.find(
                                    (field) => field in submitErrors,
                                );
                            if (invalidIdentityField) {
                                handleIdentityExpandedChange(true);
                                // After paint: the field is still hidden this
                                // tick, and focus does not land on a
                                // `display: none` input.
                                requestAnimationFrame(() => {
                                    // `name` is not registered — it is written
                                    // through `setValue` from two local inputs
                                    // — so RHF holds no ref to focus and the
                                    // card would open with the cursor nowhere.
                                    if (invalidIdentityField === 'name') {
                                        firstNameRef.current?.focus();
                                        return;
                                    }
                                    form.setFocus(invalidIdentityField);
                                });
                            }
                            // Field names only — never values.
                            const fields = Object.keys(submitErrors).join(',');
                            tracker.log('EnquiryValidationError', {
                                url: new URL(window.location.href),
                                data: {
                                    Source: source,
                                    fields,
                                    ExtraData: JSON.stringify({ fields }),
                                },
                            });
                        },
                    )}
                    className="flex flex-1 flex-col gap-5.5"
                >
                    {/* The professional's own words. They ride in the brand
                        pane on desktop, and the sheet does not render that
                        pane — without this they are simply absent on a phone,
                        which is the one surface where a supplier's "email us
                        instead" note is most likely to be acted on. */}
                    {!isDesktop && !!professional.CustomEnquiryMessage && (
                        <p className="mb-4 text-[0.9375rem] leading-snug whitespace-pre-wrap text-[#656565]">
                            {professional.CustomEnquiryMessage}
                        </p>
                    )}
                    {/* Contact details. A signed-in reader sees these as one
                        summary row until they choose to edit; a guest sees the
                        fields themselves. */}
                    <GateIdentityFields
                        identity={identity}
                        expanded={identityExpanded}
                        onExpandedChange={handleIdentityExpandedChange}
                        disabled={form.formState.disabled}
                        onSwitchAccount={onSwitchAccount}
                        dirty={identityDirty}
                    >
                        {/* Name row */}
                        <div className="grid grid-cols-2 gap-1.5">
                            <div className="flex flex-col gap-1.5">
                                <GateFieldLabel>First Name</GateFieldLabel>
                                <GateInput
                                    className={inputClass}
                                    placeholder="First Name"
                                    editHint
                                    autoComplete="given-name"
                                    disabled={form.formState.disabled}
                                    value={name.firstName}
                                    onChange={(e) =>
                                        syncName(e.target.value, name.lastName)
                                    }
                                    onBlur={() =>
                                        saveEnquirySession(form.getValues())
                                    }
                                />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <GateFieldLabel>Last Name</GateFieldLabel>
                                <GateInput
                                    className={inputClass}
                                    placeholder="Last Name"
                                    editHint
                                    autoComplete="family-name"
                                    disabled={form.formState.disabled}
                                    value={name.lastName}
                                    onChange={(e) =>
                                        syncName(name.firstName, e.target.value)
                                    }
                                    onBlur={() =>
                                        saveEnquirySession(form.getValues())
                                    }
                                />
                            </div>
                        </div>
                        <GateFieldError message={errors.name?.message} />

                        {/* Email */}
                        <div className="flex flex-col gap-1.5">
                            <GateFieldLabel>Email Address</GateFieldLabel>
                            <GateInput
                                {...emailReg}
                                className={cn(
                                    inputClass,
                                    emailDisabled && 'opacity-60',
                                )}
                                type="email"
                                placeholder="you@email.com"
                                // Not when the address is locked to the account —
                                // that is the one field here that really is
                                // read-only, and the pencil exists to tell the two
                                // apart rather than to decorate every input.
                                editHint={!emailDisabled}
                                autoComplete="email"
                                autoCapitalize="off"
                                disabled={
                                    form.formState.disabled || emailDisabled
                                }
                                onBlur={(e) => {
                                    emailReg.onBlur(e);
                                    saveEnquirySession(form.getValues());
                                }}
                            />
                            <GateFieldError message={errors.email?.message} />
                        </div>

                        {/* Phone + Suburb / Postcode. Side by side only where the
                        pane is wide enough for them: a suburb carries its
                        country ("Auckland, New Zealand"), and at half of a
                        414px sheet that truncates to "Auckland, New Zea" —
                        exactly the value the reader needs to check before
                        sending. Names stay paired above; they are short. */}
                        <div
                            className={cn(
                                'grid gap-1.5',
                                isDesktop ? 'grid-cols-2' : 'grid-cols-1',
                            )}
                        >
                            <div className="flex flex-col gap-1.5">
                                <GateFieldLabel>Phone</GateFieldLabel>
                                <GateInput
                                    {...phoneReg}
                                    className={inputClass}
                                    type="tel"
                                    placeholder="Phone number"
                                    editHint
                                    autoComplete="tel"
                                    disabled={form.formState.disabled}
                                    onBlur={(e) => {
                                        phoneReg.onBlur(e);
                                        saveEnquirySession(form.getValues());
                                    }}
                                />
                                <GateFieldError
                                    message={errors.phoneNumber?.message}
                                />
                            </div>
                            <Controller
                                name="postcode"
                                control={form.control}
                                render={({ field }) => (
                                    <div className="flex flex-col gap-1.5">
                                        <GateFieldLabel>
                                            Suburb / Postcode
                                        </GateFieldLabel>
                                        <div className="relative">
                                            <GateInput
                                                className={cn(
                                                    inputClass,
                                                    'pr-9',
                                                )}
                                                placeholder="Suburb / Postcode"
                                                // This field runs its own suburb
                                                // suggestions, so the browser's
                                                // autofill list must not cover
                                                // them. `off` is the honest way to
                                                // say that: the previous
                                                // `one-time-code` suppressed the
                                                // list by claiming to be an SMS
                                                // code field, which made iOS offer
                                                // codes from Messages here.
                                                autoComplete="off"
                                                maxLength={100}
                                                disabled={
                                                    form.formState.disabled
                                                }
                                                ref={(el) => {
                                                    postcodeInputRef.current =
                                                        el;
                                                    field.ref(el);
                                                }}
                                                value={postcodeSearch}
                                                onChange={(e) => {
                                                    setPostcodeSearch(
                                                        e.target.value,
                                                    );
                                                    setHighlightedIndex(-1);
                                                    findPlace(e.target.value);
                                                    if (e.target.value) {
                                                        setIsPostcodeSearchOpen(
                                                            true,
                                                        );
                                                    } else {
                                                        selectPostcode(
                                                            undefined,
                                                        );
                                                    }
                                                }}
                                                onFocus={(e) => {
                                                    if (postcodeSearch) {
                                                        setIsPostcodeSearchOpen(
                                                            true,
                                                        );
                                                        if (
                                                            !placeOptions.length
                                                        ) {
                                                            findPlace(
                                                                postcodeSearch,
                                                            );
                                                        }
                                                    }
                                                    e.target.select?.();
                                                }}
                                                onBlur={() => {
                                                    field.onBlur();
                                                    autoSelectPostcode();
                                                    setIsPostcodeSearchOpen(
                                                        false,
                                                    );
                                                    setHighlightedIndex(-1);
                                                }}
                                                onKeyDown={(e) => {
                                                    if (
                                                        !isPostcodeSearchOpen ||
                                                        !placeOptions.length
                                                    ) {
                                                        return;
                                                    }
                                                    switch (e.key) {
                                                        case 'ArrowDown':
                                                            e.preventDefault();
                                                            setHighlightedIndex(
                                                                (prev) =>
                                                                    prev <
                                                                    placeOptions.length -
                                                                        1
                                                                        ? prev +
                                                                          1
                                                                        : 0,
                                                            );
                                                            break;
                                                        case 'ArrowUp':
                                                            e.preventDefault();
                                                            setHighlightedIndex(
                                                                (prev) =>
                                                                    prev > 0
                                                                        ? prev -
                                                                          1
                                                                        : placeOptions.length -
                                                                          1,
                                                            );
                                                            break;
                                                        case 'Enter':
                                                            e.preventDefault();
                                                            if (
                                                                highlightedIndex >=
                                                                    0 &&
                                                                highlightedIndex <
                                                                    placeOptions.length
                                                            ) {
                                                                selectPostcode(
                                                                    placeOptions[
                                                                        highlightedIndex
                                                                    ],
                                                                );
                                                            } else {
                                                                selectAvailablePostCode();
                                                            }
                                                            break;
                                                        case 'Escape':
                                                            setIsPostcodeSearchOpen(
                                                                false,
                                                            );
                                                            setHighlightedIndex(
                                                                -1,
                                                            );
                                                            break;
                                                    }
                                                }}
                                            />
                                            {postcodeSearch && (
                                                <button
                                                    type="button"
                                                    tabIndex={-1}
                                                    className="absolute inset-y-0 right-2.5 flex items-center text-black/50 hover:text-black/80"
                                                    aria-label="Clear postcode"
                                                    onPointerDown={(e) =>
                                                        e.preventDefault()
                                                    }
                                                    onClick={() =>
                                                        selectPostcode(
                                                            undefined,
                                                        )
                                                    }
                                                >
                                                    <X className="size-4" />
                                                </button>
                                            )}
                                        </div>
                                        <PostcodePopover
                                            open={isPostcodeSearchOpen}
                                            usePortal
                                            surface="white"
                                            // The anchor is a zero-width point, so
                                            // the panel sizes itself: wide enough
                                            // for "1041, Auckland, New Zealand"
                                            // without running past the card.
                                            contentClassName="w-[17rem]"
                                            onOpenChange={(open) => {
                                                if (
                                                    !open &&
                                                    document.activeElement ===
                                                        postcodeInputRef.current
                                                ) {
                                                    return;
                                                }
                                                setIsPostcodeSearchOpen(open);
                                            }}
                                            value={field.value}
                                            placeOptions={placeOptions}
                                            isLoading={isPlaceOptionsLoading}
                                            highlightedIndex={highlightedIndex}
                                            onSelect={selectPostcode}
                                        >
                                            <div className="absolute">
                                                <PopoverAnchor className="-mt-2" />
                                            </div>
                                        </PostcodePopover>
                                        <GateFieldError
                                            message={errors.postcode?.message}
                                        />
                                    </div>
                                )}
                            />
                        </div>
                    </GateIdentityFields>

                    {/* Send to branch (only where there is a choice to make) */}
                    {showBranchPicker && (
                        <Controller
                            name="branchId"
                            control={form.control}
                            render={({ field }) => (
                                <div className="flex flex-col gap-1.5">
                                    <GateFieldLabel>
                                        Send to branch
                                    </GateFieldLabel>
                                    <Select
                                        // Radix keeps the last value if this
                                        // goes `undefined`; feed '' so an
                                        // unset branch shows the placeholder.
                                        value={field.value ?? ''}
                                        disabled={form.formState.disabled}
                                        onValueChange={(value) => {
                                            field.onChange(value);
                                            saveEnquirySession({
                                                ...form.getValues(),
                                                branchId: value,
                                            });
                                        }}
                                    >
                                        <SelectTrigger
                                            className={cn(
                                                GATE_FIELD_INPUT_CLASS,
                                                'w-full justify-between data-[placeholder]:text-black/30',
                                            )}
                                            onBlur={field.onBlur}
                                        >
                                            <SelectValue placeholder="Select a branch" />
                                        </SelectTrigger>
                                        {/* Already portalled to <body> with
                                            `position="popper"`, so the dialog's
                                            `translate` centring cannot
                                            mis-anchor it (BB-3732). Width and
                                            alignment tied to the trigger so it
                                            reads as the field opening. */}
                                        <SelectContent
                                            align="start"
                                            className="w-(--radix-select-trigger-width)"
                                        >
                                            {professional.branchOptions.map(
                                                (branch) => (
                                                    <SelectItem
                                                        key={
                                                            branch.value
                                                                .branchId
                                                        }
                                                        value={branch.value.branchId.toString()}
                                                    >
                                                        {branch.header}
                                                    </SelectItem>
                                                ),
                                            )}
                                        </SelectContent>
                                    </Select>
                                    <GateFieldError
                                        message={errors.branchId?.message}
                                    />
                                </div>
                            )}
                        />
                    )}

                    {/* Message */}
                    <div className="flex flex-col gap-1.5">
                        <GateFieldLabel>Message</GateFieldLabel>
                        <GateTextarea
                            {...messageReg}
                            className="h-32.5"
                            placeholder={
                                messagePlaceholder ??
                                'E.g. Hi there, we are planning a residential new build in Auckland and are interested in working with you. Please let us know if you’re available for a chat.'
                            }
                            autoComplete="off"
                            disabled={form.formState.disabled}
                            onBlur={(e) => {
                                messageReg.onBlur(e);
                                saveEnquirySession(form.getValues());
                            }}
                        />
                        <GateFieldError message={errors.message?.message} />
                    </div>

                    {/* Every surface. The legacy form hid this below desktop and
                        the revamp carried that across, which had it backwards:
                        a phone is where attaching is easiest — camera roll, or
                        a photo taken on the spot — and an enquiry carrying a
                        plan or a photo of the space is a far better lead than
                        the same message alone. */}
                    <FileAttachment
                        variant="compact"
                        files={files}
                        onChange={handleFileChange}
                        maxAttachments={5}
                        disabled={form.formState.disabled}
                    />

                    {/* BuildHub project details (logged-in, flags on) — all viewports, matching legacy. */}
                    {canIncludeProjectDetails && (
                        <FormField
                            name="buildHubId"
                            control={form.control}
                            render={({ field }) => (
                                <EnquiryProjectDetailsCard
                                    value={field.value}
                                    onChange={(value) => {
                                        // `setValue`, not `field.onChange`:
                                        // the controller's handler reads its
                                        // argument as an event payload and
                                        // ignores `undefined`, so detaching a
                                        // project cleared the saved draft but
                                        // left the field — and the enquiry —
                                        // still holding it.
                                        form.setValue('buildHubId', value, {
                                            shouldDirty: true,
                                        });
                                        saveEnquirySession({
                                            ...form.getValues(),
                                            buildHubId: value,
                                        });
                                    }}
                                    disabled={field.disabled}
                                    onBlur={field.onBlur}
                                    buildHubs={buildHubs}
                                    isLoading={isBuildHubsLoading}
                                    fieldSelection={fieldSelection}
                                    onFieldSelectionChange={setFieldSelection}
                                    onRefetchBuildHubs={fetchBuildHubs}
                                    professionalTitle={professional.Title}
                                />
                            )}
                        />
                    )}

                    {/* This form is the one step long enough to overflow either
                        surface, so the action is pinned on both — and pinned
                        without a bar behind it, the reader's own message scrolled
                        under the button. The footer owns the pinning, the device
                        safe area and the spacing beneath; the button is just a
                        button. */}
                    {/* `mt-6` is clearance, not decoration. The bar is pinned
                        and opaque, and it is the last thing in the scroll — so
                        it never leaves the bottom edge, and whatever sits
                        immediately above it can never be scrolled out from
                        under it. Flush against the attachment control, the
                        hairline cut the pill in half. */}
                    {/* `mt-auto` alone. A `pt-*` here is swallowed by the
                        footer's own surface padding, which `cn` applies after
                        this class — so it reads as clearance that is not
                        there. The form's `gap-5.5` is what separates the bar
                        from the last control, the same rhythm as every other
                        pair of fields above it. */}
                    <GateStickyFooter pinOn="both" className="mt-auto">
                        <button
                            type="submit"
                            disabled={disableSubmit || form.formState.disabled}
                            className="flex h-14 w-full items-center justify-center gap-2.5 rounded-full bg-[#181818] text-[1.25rem] font-medium tracking-[-0.02em] text-[#f3f3f3] transition-colors hover:bg-[#2c2c2c] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {/* The send can take several seconds. Without this
                                the only sign anything happened was the button
                                fading to its disabled colour, which reads as
                                refusal rather than progress. Same words and same
                                spinner as the pane the post-auth resume shows, so
                                one wait does not look like a different thing from
                                the other. */}
                            {isSending ? (
                                <>
                                    <Loader2 className="size-5 animate-spin" />
                                    Sending your enquiry…
                                </>
                            ) : (
                                'Continue'
                            )}
                        </button>
                    </GateStickyFooter>
                </form>
            </Form>
        </div>
    );
};
